import { caseFilterSchema } from "@/components/Forms/schema/caseFiltersetSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import type { CaseFilterEditing } from "@/queries/useFetchCaseFiltersets.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";
import { createDataSelector, createResourceSelector, handleJsonApiError } from "jsonapi-zod-query";

const resourceSelector = createResourceSelector({
    type: "caseFilter",
    attributesSchema: caseFilterSchema,
});

export const caseFilterSelector = createDataSelector(resourceSelector);
export type SavedCaseFilter = ReturnType<typeof caseFilterSelector>;

export const useSaveCaseFilter = (): UseMutationResult<
    SavedCaseFilter,
    Error,
    { caseFilter: CaseFilterEditing }
> => {
    const fetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ caseFilter }) => {
            const response = await fetch(apiUrl("/dashboard/save-case-filter"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/vnd.api+json",
                },
                body: JSON.stringify({
                    ...caseFilter,
                }),
            });
            await handleJsonApiError(response);

            const body = (await response.json()) as unknown;
            return caseFilterSelector(body);
        },
        onSuccess: async (_, {}) => {
            await queryClient.invalidateQueries({
                queryKey: ["case-filters"],
            });
        },
    });
};
